import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { PageEvent } from '@angular/material/paginator';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { AuthorizedApiService } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import {
  ColumnDefinition,
  MasterTableComponent,
  TableAction,
  TableFilterDefinition,
} from '@shared/components/master-table/master-table.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { buildOrgUnitTreeOptions } from '@shared/utils/org-unit-tree.util';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';
import { EmployeeRecord } from '../../organization/employees/employee.interface';
import { AttendanceJustificationTypeRecord } from '../attendance-justification-type/attendance-justification-type.interface';
import {
  AttendanceJustificationListResponse,
  AttendanceJustificationRecord,
  AttendanceJustificationScope,
  AttendanceJustificationStatus,
} from './attendance-justification.interface';

interface CompaniesResponse {
  data?: CompaniesRecord[];
  items?: CompaniesRecord[];
  results?: CompaniesRecord[];
  companiesRecords?: CompaniesRecord[];
}

interface OrgUnitRecord {
  orgUnitId: string;
  companiesRecordId: string;
  name: string;
  parentId?: string | null;
  type?: string | null;
  orgPath?: string | null;
  level?: number | null;
  isActive?: boolean;
}

interface OrgUnitsResponse {
  data?: OrgUnitRecord[];
  items?: OrgUnitRecord[];
  results?: OrgUnitRecord[];
  orgUnits?: OrgUnitRecord[];
}

interface EmployeesResponse {
  data?: EmployeeRecord[];
  items?: EmployeeRecord[];
  results?: EmployeeRecord[];
  employees?: EmployeeRecord[];
}

@Component({
  selector: 'app-attendance-justification',
  standalone: true,
  imports: [BreadcrumbComponent, MasterTableComponent, TranslateModule],
  templateUrl: './attendance-justification.component.html',
  styleUrl: './attendance-justification.component.scss',
})
export class AttendanceJustificationComponent implements OnInit {
  dataSource = new MatTableDataSource<AttendanceJustificationRecord>([]);
  companies: CompaniesRecord[] = [];
  orgUnits: OrgUnitRecord[] = [];
  employees: EmployeeRecord[] = [];
  justificationTypes: AttendanceJustificationTypeRecord[] = [];
  isLoading = true;
  actions: TableAction<AttendanceJustificationRecord>[] = [];
  columnDefinitions: ColumnDefinition[] = [];
  tableFilters: TableFilterDefinition<AttendanceJustificationRecord>[] = [];
  searchValue = '';
  activeFilters: Record<string, string | number | boolean | null> = {};
  currentPage = 1;
  pageSize = 15;
  totalItems = 0;
  private loadRequestSequence = 0;

  private readonly apiService = inject(AuthorizedApiService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private searchDebounceHandle: ReturnType<typeof setTimeout> | null = null;
  private employeeSearchDebounceHandle: ReturnType<typeof setTimeout> | null = null;

  readonly statusOptions: AttendanceJustificationStatus[] = ['submitted', 'approved', 'rejected', 'cancelled'];
  readonly scopeOptions: AttendanceJustificationScope[] = ['full_day', 'partial_minutes'];

  ngOnInit(): void {
    this.configureTranslations();
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.configureTranslations();
      this.tableFilters = this.buildTableFilters();
      this.dataSource.data = this.dataSource.data.map((item) => this.normalizeAttendanceJustification(item));
    });
    void this.initializeData();
  }

  async initializeData(): Promise<void> {
    this.isLoading = true;
    try {
      await this.loadCompanies();
      await this.loadJustificationTypes();
      await this.loadDependentLookups();
      this.tableFilters = this.buildTableFilters();
      await this.loadData();
    } finally {
      this.isLoading = false;
    }
  }

  async loadData(): Promise<void> {
    const requestId = ++this.loadRequestSequence;
    this.isLoading = true;
    try {
      const response = await firstValueFrom(
        this.apiService.get<AttendanceJustificationListResponse>('attendance-justifications', {
          params: {
            page: this.currentPage,
            perPage: this.pageSize,
            ...(this.searchValue ? { search: this.searchValue } : {}),
            ...(this.getActiveCompanyId() ? { companiesRecordId: this.getActiveCompanyId() } : {}),
            ...(this.getActiveOrgUnitId() ? { orgUnitId: this.getActiveOrgUnitId() } : {}),
            ...(this.getActiveEmployeeId() ? { employeeId: this.getActiveEmployeeId() } : {}),
            ...(this.getActiveTypeId() ? { attendanceJustificationTypeId: this.getActiveTypeId() } : {}),
            ...(this.getActiveWorkDate() ? { workDate: this.getActiveWorkDate() } : {}),
            ...(this.getActiveStatus() ? { status: this.getActiveStatus() } : {}),
            ...(this.getActiveScope() ? { scope: this.getActiveScope() } : {}),
            ...(this.getActiveAdministrativeStatus() !== '' ? { isActive: this.getActiveAdministrativeStatus() } : {}),
          },
        })
      );

      if (requestId !== this.loadRequestSequence) return;

      this.totalItems = response?.pagination?.total || 0;
      this.dataSource.data = (response?.data || []).map((item) => this.normalizeAttendanceJustification(item));
      this.tableFilters = this.buildTableFilters();
    } catch (error) {
      if (requestId !== this.loadRequestSequence) return;
      console.error('Error loading attendance justifications', error);
      this.dataSource.data = [];
      this.totalItems = 0;
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('attendanceJustificationsCatalog.notifications.loadError')),
        'bottom',
        'center'
      );
    } finally {
      if (requestId === this.loadRequestSequence) this.isLoading = false;
    }
  }

  handleAdd(): void {
    if (!this.hasAccess('attendanceJustifications', 'create')) {
      return;
    }
    void this.router.navigate(['/attendance/attendance-justifications/new']);
  }

  async handleView(row: AttendanceJustificationRecord): Promise<void> {
    if (!this.hasAccess('attendanceJustifications', 'read')) {
      return;
    }
    await this.router.navigate(['/attendance/attendance-justifications', row.attendanceJustificationId]);
  }

  async handleEdit(row: AttendanceJustificationRecord): Promise<void> {
    if (!this.hasAccess('attendanceJustifications', 'update')) {
      return;
    }
    if (row.status !== 'submitted') {
      this.showNotification('snackbar-warning', this.t('attendanceJustificationsCatalog.notifications.editOnlySubmitted'), 'bottom', 'center');
      return;
    }
    await this.router.navigate(['/attendance/attendance-justifications', row.attendanceJustificationId, 'edit']);
  }

  handleDelete(row: AttendanceJustificationRecord): void {
    if (!this.hasAccess('attendanceJustifications', 'delete')) {
      return;
    }
    if (row.status === 'approved') {
      this.showNotification('snackbar-warning', this.t('attendanceJustificationsCatalog.notifications.deleteApprovedBlocked'), 'bottom', 'center');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('attendanceJustificationsCatalog.dialog.deleteTitle'),
        message: this.t('attendanceJustificationsCatalog.dialog.deleteMessage', { justification: row.typeLabel }),
        confirmLabel: this.t('attendanceJustificationsCatalog.dialog.deleteConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await firstValueFrom(this.apiService.delete(`attendance-justifications/${row.attendanceJustificationId}`));
        await this.loadData();
        this.showNotification('snackbar-success', this.t('attendanceJustificationsCatalog.notifications.deleted'), 'bottom', 'center');
      } catch (error) {
        console.error('Error deleting attendance justification', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('attendanceJustificationsCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  handleBulkDelete(rows: AttendanceJustificationRecord[]): void {
    if (!this.hasAccess('attendanceJustifications', 'delete') || !rows.length) {
      return;
    }
    const blocked = rows.some((row) => row.status === 'approved');
    if (blocked) {
      this.showNotification('snackbar-warning', this.t('attendanceJustificationsCatalog.notifications.bulkDeleteApprovedBlocked'), 'bottom', 'center');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('common.bulkDeleteTitle'),
        message: this.t('common.bulkDeleteMessage', { count: rows.length }),
        confirmLabel: this.t('common.delete'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await Promise.all(rows.map((row) => firstValueFrom(this.apiService.delete(`attendance-justifications/${row.attendanceJustificationId}`))));
        await this.loadData();
        this.showNotification('snackbar-success', this.t('common.bulkDeleteSuccess', { count: rows.length }), 'bottom', 'center');
      } catch (error) {
        console.error('Error deleting attendance justifications', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('attendanceJustificationsCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  onSearchChange(search: string): void {
    this.searchValue = search.trim();
    if (this.searchDebounceHandle) {
      clearTimeout(this.searchDebounceHandle);
    }
    this.searchDebounceHandle = setTimeout(() => {
      this.currentPage = 1;
      void this.loadData();
    }, 300);
  }

  async onFiltersChange(filters: Record<string, string | number | boolean | null>): Promise<void> {
    const previousCompanyId = this.getActiveCompanyId();
    const previousOrgUnitId = this.getActiveOrgUnitId();
    this.activeFilters = filters;

    if (previousCompanyId !== this.getActiveCompanyId()) {
      this.activeFilters['orgUnit'] = null;
      this.activeFilters['employee'] = null;
      await this.loadDependentLookups();
    } else if (previousOrgUnitId !== this.getActiveOrgUnitId()) {
      this.activeFilters['employee'] = null;
      await this.loadEmployees(this.getActiveCompanyId());
    }

    if (this.getActiveOrgUnitId() && this.getActiveEmployeeId()) {
      const employee = this.employees.find((item) => item.employeeId === this.getActiveEmployeeId());
      if (employee?.orgUnitId !== this.getActiveOrgUnitId()) {
        this.activeFilters['employee'] = null;
      }
    }

    this.currentPage = 1;
    this.tableFilters = this.buildTableFilters();
    await this.loadData();
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    void this.loadData();
  }

  onFilterSearchChange(event: { filterId: string; search: string }): void {
    if (event.filterId !== 'employee') {
      return;
    }

    if (this.employeeSearchDebounceHandle) {
      clearTimeout(this.employeeSearchDebounceHandle);
    }

    this.employeeSearchDebounceHandle = setTimeout(() => {
      void this.loadEmployees(this.getActiveCompanyId(), event.search);
    }, 250);
  }

  private async loadCompanies(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.apiService.get<CompaniesRecord[] | CompaniesResponse>('companies-records', {
          params: { isActive: 'true' },
        })
      );
      this.companies = this.extractCompanies(response)
        .filter((company) => company.isActive !== false)
        .sort((left, right) => left.name.localeCompare(right.name));
    } catch (error) {
      console.error('Error loading companies for attendance justifications', error);
      this.companies = [];
    }
  }

  private async loadJustificationTypes(): Promise<void> {
    try {
      const response = await firstValueFrom(this.apiService.get<AttendanceJustificationTypeRecord[]>('attendance-justification-types'));
      this.justificationTypes = (response || [])
        .filter((item) => item.isActive !== false)
        .sort((left, right) => (left.label || '').localeCompare(right.label || ''));
    } catch (error) {
      console.error('Error loading justification types for attendance justifications', error);
      this.justificationTypes = [];
    }
  }

  private async loadDependentLookups(): Promise<void> {
    const companyId = this.getActiveCompanyId();
    await Promise.all([this.loadOrgUnits(companyId), this.loadEmployees(companyId)]);
  }

  private async loadOrgUnits(companyId: string): Promise<void> {
    try {
      const response = await firstValueFrom(this.apiService.get<OrgUnitRecord[] | OrgUnitsResponse>('org-units'));
      const companyUnits = this.extractOrgUnits(response)
        .filter((unit) => (companyId ? unit.companiesRecordId === companyId : true) && unit.isActive !== false);
      this.orgUnits = companyUnits
        .sort((left, right) => left.name.localeCompare(right.name));
    } catch (error) {
      console.error('Error loading org units for attendance justifications', error);
      this.orgUnits = [];
    }
  }

  private async loadEmployees(companyId: string, search = ''): Promise<void> {
    try {
      const selectedEmployeeId = this.getActiveEmployeeId();
      const selectedEmployee = this.employees.find((employee) => employee.employeeId === selectedEmployeeId);
      const params: Record<string, string | number> = { page: 1, perPage: 20, isActive: 'true' };
      if (companyId) {
        params['companiesRecordId'] = companyId;
      }
      if (this.getActiveOrgUnitId()) {
        params['orgUnitId'] = this.getActiveOrgUnitId();
      }
      if (search) {
        params['search'] = search;
      }
      const response = await firstValueFrom(this.apiService.get<EmployeeRecord[] | EmployeesResponse>('employees', { params }));
      const nextEmployees = this.extractEmployees(response)
        .filter((employee) => (companyId ? employee.companiesRecordId === companyId : true))
        .sort((left, right) => (left.fullName || '').localeCompare(right.fullName || ''));

      this.employees = selectedEmployee && !nextEmployees.some((employee) => employee.employeeId === selectedEmployee.employeeId)
        ? [selectedEmployee, ...nextEmployees]
        : nextEmployees;

      this.tableFilters = this.buildTableFilters();
    } catch (error) {
      console.error('Error loading employees for attendance justifications', error);
      this.employees = [];
    }
  }

  private extractCompanies(response: CompaniesRecord[] | CompaniesResponse): CompaniesRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.companiesRecords || [];
  }

  private extractOrgUnits(response: OrgUnitRecord[] | OrgUnitsResponse): OrgUnitRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.orgUnits || [];
  }

  private extractEmployees(response: EmployeeRecord[] | EmployeesResponse): EmployeeRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.employees || [];
  }

  private normalizeAttendanceJustification(item: Partial<AttendanceJustificationRecord>): AttendanceJustificationRecord {
    const status = this.normalizeText(item.status) as AttendanceJustificationStatus || 'submitted';
    const scope = this.normalizeText(item.scope) as AttendanceJustificationScope || 'full_day';
    const employee = this.employees.find((record) => record.employeeId === item.employeeId);
    const companyName = this.getCompanyName(item.companiesRecordId);
    const orgUnitName = this.getOrgUnitName(item.orgUnitId);
    const typeLabel = item.typeLabel || this.justificationTypes.find((type) => type.attendanceJustificationTypeId === item.attendanceJustificationTypeId)?.label || '-';
    const isActive = item.isActive !== false;
    const minutes = item.minutes ?? null;

    return {
      attendanceJustificationId: item.attendanceJustificationId || crypto.randomUUID(),
      center: item.center || '',
      employeeId: item.employeeId || '',
      companiesRecordId: item.companiesRecordId || '',
      orgUnitId: item.orgUnitId || null,
      orgPath: item.orgPath || null,
      attendanceJustificationTypeId: item.attendanceJustificationTypeId || '',
      typeCode: item.typeCode || '',
      typeLabel,
      scope,
      status,
      workDate: item.workDate || '',
      minutes,
      payEffect: (this.normalizeText(item.payEffect) as any) || 'paid',
      attendanceEffect: (this.normalizeText(item.attendanceEffect) as any) || 'justify_day',
      comment: item.comment || '',
      attachments: Array.isArray(item.attachments) ? item.attachments : [],
      submittedByUid: item.submittedByUid || null,
      submittedAt: item.submittedAt || null,
      resolvedByUid: item.resolvedByUid || null,
      resolvedAt: item.resolvedAt || null,
      resolutionType: (item.resolutionType as any) || null,
      resolutionComment: item.resolutionComment || null,
      history: Array.isArray(item.history) ? item.history : [],
      isActive,
      createdAt: item.createdAt || null,
      updatedAt: item.updatedAt || null,
      employeeNumber: item.employeeNumber || employee?.employeeNumber || '-',
      employeeLabel: employee?.fullName || item.employeeId || '-',
      companyName,
      orgUnitName,
      scopeLabel: this.t(`attendanceJustificationsCatalog.scopeOptions.${scope}`),
      statusLabel: this.t(`attendanceJustificationsCatalog.statuses.${status}`),
      payEffectLabel: this.t(`attendanceJustificationsCatalog.payEffectOptions.${item.payEffect || 'paid'}`),
      attendanceEffectLabel: this.t(`attendanceJustificationsCatalog.attendanceEffectOptions.${item.attendanceEffect || 'justify_day'}`),
      activeLabel: isActive ? this.t('common.status.active') : this.t('common.status.inactive'),
      minutesLabel: this.formatMinutes(minutes),
      workDateLabel: this.formatDate(item.workDate),
      submittedAtLabel: this.formatDateTime(item.submittedAt),
      resolvedAtLabel: this.formatDateTime(item.resolvedAt),
      createdAtLabel: this.formatDateTime(item.createdAt),
      updatedAtLabel: this.formatDateTime(item.updatedAt),
      resolutionTypeLabel: item.resolutionType ? this.t(`attendanceJustificationsCatalog.resolutionTypes.${item.resolutionType}`) : '-',
    };
  }

  private buildTableFilters(): TableFilterDefinition<AttendanceJustificationRecord>[] {
    return [
      {
        id: 'company',
        label: this.t('attendanceJustificationsCatalog.filters.company'),
        placeholder: this.t('attendanceJustificationsCatalog.filters.companyPlaceholder'),
        icon: 'business',
        searchable: true,
        value: this.activeFilters['company'] ?? null,
        options: this.companies.map((company) => ({ value: company.companiesRecordId, label: company.name })),
      },
      {
        id: 'orgUnit',
        label: this.t('attendanceJustificationsCatalog.filters.orgUnit'),
        placeholder: this.t('attendanceJustificationsCatalog.filters.orgUnitPlaceholder'),
        icon: 'account_tree',
        type: 'tree-select',
        multiple: false,
        searchPlaceholder: this.t('attendanceJustificationsCatalog.filters.orgUnitPlaceholder'),
        value: this.activeFilters['orgUnit'] ?? null,
        treeOptions: buildOrgUnitTreeOptions(this.orgUnits),
      },
      {
        id: 'employee',
        label: this.t('attendanceJustificationsCatalog.filters.employee'),
        placeholder: this.t('attendanceJustificationsCatalog.filters.employeePlaceholder'),
        icon: 'badge',
        searchable: true,
        value: this.activeFilters['employee'] ?? null,
        options: this.employees.map((employee) => ({
          value: employee.employeeId,
          label: `${employee.fullName || employee.employeeNumber} · ${employee.employeeNumber}`,
          searchText: `${employee.fullName || ''} ${employee.employeeNumber || ''}`.trim(),
        })),
      },
      {
        id: 'type',
        label: this.t('attendanceJustificationsCatalog.filters.type'),
        placeholder: this.t('attendanceJustificationsCatalog.filters.typePlaceholder'),
        icon: 'fact_check',
        searchable: true,
        value: this.activeFilters['type'] ?? null,
        options: this.justificationTypes.map((type) => ({ value: type.attendanceJustificationTypeId, label: type.label })),
      },
      {
        id: 'workDate',
        label: this.t('attendanceJustificationsCatalog.filters.workDate'),
        placeholder: this.t('attendanceJustificationsCatalog.filters.workDatePlaceholder'),
        icon: 'calendar_today',
        type: 'date',
        value: this.activeFilters['workDate'] ?? null,
      },
      {
        id: 'status',
        label: this.t('attendanceJustificationsCatalog.filters.status'),
        placeholder: this.t('attendanceJustificationsCatalog.filters.statusPlaceholder'),
        icon: 'rule',
        searchable: false,
        value: this.activeFilters['status'] ?? null,
        options: this.statusOptions.map((status) => ({ value: status, label: this.t(`attendanceJustificationsCatalog.statuses.${status}`) })),
      },
      {
        id: 'scope',
        label: this.t('attendanceJustificationsCatalog.filters.scope'),
        placeholder: this.t('attendanceJustificationsCatalog.filters.scopePlaceholder'),
        icon: 'schedule',
        searchable: false,
        value: this.activeFilters['scope'] ?? null,
        options: this.scopeOptions.map((scope) => ({ value: scope, label: this.t(`attendanceJustificationsCatalog.scopeOptions.${scope}`) })),
      },
      {
        id: 'administrativeStatus',
        label: this.t('attendanceJustificationsCatalog.fields.administrativeStatus'),
        placeholder: this.t('attendanceJustificationsCatalog.filters.administrativeStatusPlaceholder'),
        icon: 'verified',
        searchable: false,
        value: this.activeFilters['administrativeStatus'] ?? null,
        options: [
          { value: 'true', label: this.t('common.status.active') },
          { value: 'false', label: this.t('common.status.inactive') },
        ],
      },
    ];
  }

  private configureTranslations(): void {
    this.actions = [
      {
        id: 'add',
        icon: 'add',
        tooltip: this.t('attendanceJustificationsCatalog.actions.create'),
        colorClass: 'success',
        auth: { resource: 'attendanceJustifications', action: 'create' },
        behavior: 'add',
      },
      {
        id: 'details',
        icon: 'visibility',
        tooltip: this.t('attendanceJustificationsCatalog.actions.view'),
        colorClass: 'info',
        auth: { resource: 'attendanceJustifications', action: 'read' },
        selectionMode: 'single',
      },
      {
        id: 'edit',
        icon: 'edit',
        tooltip: this.t('common.update'),
        colorClass: 'accent',
        auth: { resource: 'attendanceJustifications', action: 'update' },
        behavior: 'edit',
        selectionMode: 'single',
      },
      {
        id: 'delete',
        icon: 'delete_outline',
        tooltip: this.t('common.delete'),
        colorClass: 'danger',
        auth: { resource: 'attendanceJustifications', action: 'delete' },
        behavior: 'delete',
        selectionMode: 'oneOrMore',
      },
    ];

    this.columnDefinitions = [
      { def: 'select', label: this.t('common.select'), type: 'check', visible: true },
      { def: 'employeeNumber', label: this.t('attendanceJustificationsCatalog.fields.employeeNumber'), type: 'text', visible: true },
      { def: 'employeeLabel', label: this.t('attendanceJustificationsCatalog.fields.employee'), type: 'text', visible: true },
      { def: 'typeLabel', label: this.t('attendanceJustificationsCatalog.fields.type'), type: 'text', visible: true },
      { def: 'scopeLabel', label: this.t('attendanceJustificationsCatalog.fields.scope'), type: 'text', visible: true },
      { def: 'workDateLabel', label: this.t('attendanceJustificationsCatalog.fields.workDate'), type: 'text', visible: true },
      { def: 'statusLabel', label: this.t('attendanceJustificationsCatalog.fields.status'), type: 'status', visible: true },
      { def: 'minutesLabel', label: this.t('attendanceJustificationsCatalog.fields.minutes'), type: 'text', visible: true },
      { def: 'payEffectLabel', label: this.t('attendanceJustificationsCatalog.fields.payEffect'), type: 'text', visible: true },
      { def: 'activeLabel', label: this.t('attendanceJustificationsCatalog.fields.administrativeStatus'), type: 'status', visible: true },
    ];

    this.tableFilters = this.buildTableFilters();
  }

  private getCompanyName(companyId: string | null | undefined): string {
    if (!companyId) {
      return this.t('attendanceJustificationsCatalog.labels.noCompany');
    }
    return this.companies.find((company) => company.companiesRecordId === companyId)?.name || this.t('attendanceJustificationsCatalog.labels.noCompany');
  }

  private getOrgUnitName(orgUnitId: string | null | undefined): string {
    if (!orgUnitId) {
      return this.t('attendanceJustificationsCatalog.labels.noOrgUnit');
    }
    return this.orgUnits.find((unit) => unit.orgUnitId === orgUnitId)?.name || orgUnitId;
  }

  private formatDate(value?: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  }

  private formatDateTime(value?: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  private formatMinutes(value?: number | null): string {
    if (value === null || value === undefined) {
      return '-';
    }
    return `${value} min`;
  }

  private getActiveCompanyId(): string {
    return (this.activeFilters['company'] as string) || '';
  }

  private getActiveOrgUnitId(): string {
    return (this.activeFilters['orgUnit'] as string) || '';
  }

  private getActiveEmployeeId(): string {
    return (this.activeFilters['employee'] as string) || '';
  }

  private getActiveTypeId(): string {
    return (this.activeFilters['type'] as string) || '';
  }

  private getActiveWorkDate(): string {
    return (this.activeFilters['workDate'] as string) || '';
  }

  private getActiveStatus(): string {
    return (this.activeFilters['status'] as string) || '';
  }

  private getActiveScope(): string {
    return (this.activeFilters['scope'] as string) || '';
  }

  private getActiveAdministrativeStatus(): string {
    const value = this.activeFilters['administrativeStatus'];
    if (value === null || value === undefined || value === '') {
      return '';
    }
    return String(value);
  }

  private normalizeText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private hasAccess(resource: string, action: string): boolean {
    return this.authorizationService.hasAccess({ resource, action });
  }

  private showNotification(colorName: string, text: string, placementFrom: MatSnackBarVerticalPosition, placementAlign: MatSnackBarHorizontalPosition): void {
    this.snackBar.open(text, '', {
      duration: 2400,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
